import { EMPTY_SELECT_VALUE, fromSelectValue, toSelectValue } from '@/lib/select-value';
import { useIsVitrine } from '@/lib/use-instance';
import type { VersionedWording } from '@/lib/versioned-labels';
import { REFERENT_NOWHERE, TAG_FILTER_LABEL } from '@/lib/versioned-labels';
import type { VersionedFilters, VersionedTagFilter } from '@matter_extractor/shared';
import { versionedTagFilterSchema } from '@matter_extractor/shared';
import { Button, Select, TextField } from '@pms-digital/design-system';
import type { ReactNode } from 'react';

/**
 * The five filters that are ALWAYS visible, and the door to the others
 * (refonte modulaire §4; maquette `Main.dc.html`).
 *
 * The five are common to every versioned object, which is why this row knows
 * none of them by name beyond what `versionedFiltersShape` declares. What sits
 * behind the button is the panel, and that is where an object's own filters
 * live.
 *
 * §4 fixes both the five and the split: « les cinq premiers sont toujours
 * visibles, les autres derrière "Plus de filtres" ». The five are the ones an
 * operator reaches for without thinking - a name, a tag, who uses it, who wrote
 * it - and the panel holds the ones that answer a question somebody came with.
 *
 * ## « Plus de filtres » is pushed to the RIGHT edge
 *
 * `ml-auto`, and the maquette draws it there deliberately: the button is not a
 * sixth filter, it is the boundary of the row. Sitting next to « Auteur » it
 * would read as one more thing to choose; at the far edge it reads as « there
 * is more ». It carries `aria-expanded` because that is what makes the state
 * legible to somebody who cannot see the panel move.
 *
 * ## The referent selects have two options and admit it
 *
 * « Tous » and « Nulle part », and the reason is at `REFERENT_NOWHERE`: a card
 * says usage as a count per kind, no route serves the referent list yet, and
 * nothing cites anything until the protocol lot lands. A select full of names
 * the page cannot resolve would empty the grid silently.
 *
 * ## And why an object may REPLACE those two (issue #55)
 *
 * The two middle slots hold the referent filters of §4 - « qui m'utilise » -
 * and for three of the four objects that is what an operator asks. For an
 * EXPERIENCE it is not: its referents are the links of §5.1 (§4, H25), so a
 * « Protocole : nulle part » on that screen would filter on a relation nothing
 * can have, and an « Expérience » select on the Expériences library reads as a
 * bug. §15 answers by naming the two visible slots of that screen « Protocole »
 * and « Persona » - the two references an experience CARRIES, the other
 * direction of the same arrow - and the mockup (`Main.dc.html`) draws them
 * there.
 *
 * So `renderReferents` REPLACES the pair, and nothing else: the name, the tag,
 * the author and « Plus de filtres » stay the layer's, in the same order, at the
 * same widths. Optional, and falling back to the two common selects, so the
 * three screens that came before emit the same nodes they always did.
 *
 * ## The package fields, at the design system's size (§9 of `recherche-sessions.md`)
 *
 * « Nom » is the package `TextField` in its search form - the magnifier, and the cross
 * while it holds something - and the four others its `Select` (Radix, not a native
 * `<select>`), all `snug`: 44 px at 16 px, the height of « Plus de filtres » beside them -
 * the package's default field is 53 px, which left the button short of the row (main drew
 * all of them at one height) - with the default label: 14 px, Regular, black, in sentence
 * case. Their `data-testid` sits on the `<input>` for « Nom », where the e2e fill it, and on
 * a wrapper for the selects, where the suites find the `role="combobox"` inside; the neutral
 * choice crosses the select as the sentinel of `@/lib/select-value`. The package field has no
 * « armed » border: the chips below are what says which filters are on.
 *
 * ## The casts on `onChange`
 *
 * `{ name: '...' }` is a patch of the COMMON filters, and every object's filter
 * type carries those thirteen fields - but TypeScript cannot see that through
 * an unresolved type parameter, so each patch is widened once. It pays for the
 * generic and for nothing an object could disagree about.
 */

export interface FilterBarProps<Filters extends VersionedFilters> {
  wording: VersionedWording;
  filters: Filters;
  /** Every author the list offers - « a créé ou a modifié », one filter (§4). */
  authors: readonly string[];
  expanded: boolean;
  onToggleExpanded: () => void;
  /**
   * The two middle selects, when the object's are not the referents of §4.
   *
   * Absent: « Protocole » and « Expérience », the two referent filters every
   * versioned object carries. See the head for why an experience replaces them.
   */
  renderReferents?: ReactNode;
  /** One dimension at a time; the caller merges and writes the URL. */
  onChange: (patch: Partial<Filters>) => void;
}

export function FilterBar<Filters extends VersionedFilters>({
  wording,
  filters,
  authors,
  expanded,
  onToggleExpanded,
  renderReferents,
  onChange,
}: FilterBarProps<Filters>) {
  // No « Tag » on the vitrine: it has one state, so there is nothing to filter by. Nor on an
  // object with one side only (npc.md N5): its tag would always read « en développement ».
  const vitrine = useIsVitrine() || wording.developmentOnly === true;

  return (
    /*
      The fields keep their width and the row WRAPS: when they leave no room, « Plus de filtres »
      goes to a line of its own, pushed to the right edge (`ml-auto`). Squeezing everything onto
      one line was tried (28/10) and made the fields too tight.
    */
    <div className="flex flex-wrap items-end gap-2.5">
      <div className="w-55 max-w-full">
        {/*
          CONTROLLED, and typing filters as it goes - unlike the reference
          search of `/admin/entretiens`, which submits. The difference is where
          the answer comes from: that screen asks the server per query, this one
          holds the whole library in one cache entry (one list schema), so
          a keystroke costs a `filter()` and not a round trip.
        */}
        <TextField
          snug
          label="Nom"
          type="search"
          icon="search"
          onClear={() => onChange({ name: '' } as Partial<Filters>)}
          clearLabel="Effacer la recherche"
          data-testid={`${wording.entity}-filtre-nom`}
          value={filters.name}
          placeholder="Rechercher"
          onChange={(event) => onChange({ name: event.currentTarget.value } as Partial<Filters>)}
        />
      </div>

      {vitrine ? null : (
        <div className="w-40 max-w-full" data-testid={`${wording.entity}-filtre-tag`}>
          <Select
            snug
            label="Tag"
            value={filters.tag}
            options={versionedTagFilterSchema.options.map((option) => ({
              value: option,
              label: TAG_FILTER_LABEL[option],
            }))}
            onValueChange={(value) =>
              onChange({ tag: value as VersionedTagFilter } as Partial<Filters>)
            }
          />
        </div>
      )}

      {renderReferents ?? (
        <>
          <div className="w-45 max-w-full" data-testid={`${wording.entity}-filtre-protocole`}>
            <Select
              snug
              label="Protocole"
              value={toSelectValue(filters.protocolId ?? '')}
              options={[
                { value: EMPTY_SELECT_VALUE, label: 'Tous' },
                { value: REFERENT_NOWHERE, label: 'Nulle part' },
              ]}
              onValueChange={(value) =>
                onChange({
                  protocolId: fromSelectValue(value) === '' ? null : REFERENT_NOWHERE,
                } as Partial<Filters>)
              }
            />
          </div>

          <div className="w-45 max-w-full" data-testid={`${wording.entity}-filtre-experience`}>
            <Select
              snug
              label="Expérience"
              value={toSelectValue(filters.experienceId ?? '')}
              options={[
                { value: EMPTY_SELECT_VALUE, label: 'Toutes' },
                { value: REFERENT_NOWHERE, label: 'Nulle part' },
              ]}
              onValueChange={(value) =>
                onChange({
                  experienceId: fromSelectValue(value) === '' ? null : REFERENT_NOWHERE,
                } as Partial<Filters>)
              }
            />
          </div>
        </>
      )}

      <div className="w-45 max-w-full" data-testid={`${wording.entity}-filtre-auteur`}>
        <Select
          snug
          label="Auteur"
          value={toSelectValue(filters.author ?? '')}
          options={[
            { value: EMPTY_SELECT_VALUE, label: 'Tous' },
            ...authors.map((author) => ({ value: author, label: author })),
          ]}
          onValueChange={(value) => {
            const author = fromSelectValue(value);
            onChange({ author: author === '' ? null : author } as Partial<Filters>);
          }}
        />
      </div>

      <div className="ml-auto">
        <Button
          type="button"
          variant="accent"
          icon="tune"
          data-testid={`${wording.entity}-plus-de-filtres`}
          aria-expanded={expanded}
          onClick={onToggleExpanded}
        >
          {expanded ? 'Moins de filtres' : 'Plus de filtres'}
        </Button>
      </div>
    </div>
  );
}
